import React, { useState } from 'react';
import { useAppSelector } from '../../store/hooks';
import { sendAdminEmail } from '../../api/admin';
import { FiSend } from 'react-icons/fi';

const AdminEmail: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [userIds, setUserIds] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ sent: number; failed: number } | null>(null);

  const handleSend = async () => {
    if (!subject || !htmlContent) return;
    setSending(true);
    setResult(null);
    try {
      const ids = sendToAll ? [] : userIds.split(',').map((s) => parseInt(s.trim())).filter((n) => !isNaN(n));
      const res = await sendAdminEmail({
        user_ids: ids,
        send_to_all: sendToAll,
        subject,
        html_content: htmlContent,
      });
      setResult({ sent: res.data.sent, failed: res.data.failed });
    } catch {
      setResult({ sent: 0, failed: -1 });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Send Email</h1>

      <div className="space-y-4">
        {/* Recipients */}
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Recipients</label>
          <div className="flex items-center gap-3 mb-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: colors.textColor }}>
              <input type="checkbox" checked={sendToAll} onChange={(e) => setSendToAll(e.target.checked)} />
              Send to all users
            </label>
          </div>
          {!sendToAll && (
            <input
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
              placeholder="User IDs (comma-separated, e.g., 1, 2, 5)"
              className="w-full px-3 py-2 rounded-xl text-sm outline-none"
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
            />
          )}
        </div>

        {/* Subject */}
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
          />
        </div>

        {/* HTML Content */}
        <div>
          <label className="text-sm font-medium mb-1 block" style={{ color: colors.grayScale500 }}>HTML Content</label>
          <textarea
            value={htmlContent}
            onChange={(e) => setHtmlContent(e.target.value)}
            placeholder="<h1>Hello!</h1><p>Your email content here...</p>"
            rows={12}
            className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-y font-mono"
            style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
          />
        </div>

        {/* Preview */}
        {htmlContent && (
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Preview</label>
            <div
              className="rounded-xl p-4 text-sm"
              style={{ backgroundColor: '#fff', border: `1px solid ${colors.borderColor}`, color: '#333' }}
              dangerouslySetInnerHTML={{ __html: htmlContent }}
            />
          </div>
        )}

        {/* Send */}
        <button
          onClick={handleSend}
          disabled={sending || !subject || !htmlContent}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: colors.primary }}
        >
          {React.createElement(FiSend as any, { size: 16 })}
          {sending ? 'Sending...' : 'Send Email'}
        </button>

        {/* Result */}
        {result && (
          <div className="rounded-xl p-4 text-sm" style={{ backgroundColor: colors.inputBackground }}>
            {result.failed === -1 ? (
              <p style={{ color: '#EF4444' }}>Failed to send emails</p>
            ) : (
              <p style={{ color: colors.textColor }}>
                Sent: <strong>{result.sent}</strong> | Failed: <strong>{result.failed}</strong>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmail;
